/**
 * Real employee directory resolver hook (C2, #22326) — replaces the
 * interim dev-bypass-only `dev_employees.ts` (deleted) the same way C1's
 * `use_session_facts.ts` replaced `dev_session_facts.ts` and C7's
 * `use_my_pay.ts` replaced its own dev-bypass-only predecessor: a
 * self-fetching hook, NOT a `dev_*.ts`/`useDevBackendData` boot-time seed —
 * see `use_my_pay.ts`'s module docblock for the same "why": this is real
 * production OAuth-gated auth work, live in every environment (including
 * production), not just local dev.
 *
 * Uses the real bearer client (`getTrpcClient()`, `lib/trpc.ts`) and the
 * shared `useOAuthEntityContext()` helper (`oauth_entity_context.ts`,
 * introduced by C1) — never `firm.list`/`firm.create`/`entity.list`. One
 * call: `payroll.listEmployees({ firm_id, entity_id })`, already allowlisted
 * behind `payroll:employer:read`/`payroll:employer:write`
 * (`apps/api/src/trpc/oauth_allowlist.ts`).
 *
 * There is NO mock fallback here — same stance as `use_my_pay.ts`/
 * `use_session_facts.ts`: a mock employee roster shown to an admin as their
 * own real company's payroll directory would be fabricated financial data,
 * not a harmless placeholder. Every failure surfaces as a real
 * `EmployeesState` (`no_entity`/`entitlement_required`/`error`) instead —
 * `MOCK_EMPLOYEES` is no longer imported or reachable from this module, and
 * `wagepro_store.tsx` no longer owns an `employees` field for any component
 * to read a mock fallback from (see that file's own comment on the removal).
 *
 * `entitlement_required` mirrors the epic doc's documented non-goal: real
 * customers are not yet granted the `payroll` add-on entitlement (#21675),
 * and `payroll.listEmployees` throws FORBIDDEN via `requirePayrollAddon`
 * until that entitlement is granted for the entity. The former
 * `dev_employees.ts` degraded that FORBIDDEN to `undefined` (the mock
 * directory); this hook turns it into an explicit, distinguishable state
 * instead (`isForbiddenTrpcError`, `lib/trpc.ts` — every `dev_*.ts` resolver
 * that used to depend on the now-deleted `lib/auth/trpc_errors.ts` copy is
 * itself deleted as of C12, #22334, so this hook now uses the same shared
 * classifier every other real, OAuth-backed hook already does).
 *
 * `deriveEmployeesFromRecords` (`wagepro_store_employees.ts`) is a permanent
 * read-adapter from the real wire shape to the UI's `Employee` type — this
 * hook is its only call site (moved here from `wagepro_store.tsx`'s
 * provider-mount-time `useState` initializer). It no longer takes a `mock`
 * fallback parameter (removed in this same PR's adversarial-review pass,
 * finding #10): every call site here always supplies defined `records` (see
 * `resolveEmployeesState` below), so the old mock-passthrough branch was
 * dead code, not defensive code — `MOCK_EMPLOYEES` is unreachable from this
 * module by construction, not by convention.
 *
 * `resolveEmployeesState`/`EmployeesState` deliberately live in THIS module
 * rather than in `wagepro_store_employees.ts` (unlike `resolveMyPayState`,
 * which lives in `wagepro_store_my_pay.ts` alongside its own adapter) — the
 * issue that tracks this pass (#22326) calls out `deriveEmployeesFromRecords`
 * as unchanged, and keeping that file byte-for-byte untouched is the more
 * conservative reading of that instruction.
 *
 * `fetchEmployeeRecords` (below) is extracted out of this hook's own fetch
 * effect, DI-testable and exported, mirroring `use_pay_runs.ts`'s
 * `fetchPayRunRecords` (C3, #22327, adversarial-review finding 1: the real
 * hook's catch-block wiring needs its own direct test coverage, not just the
 * pure `resolveEmployeesState` in isolation). C4 (#22328, `use_dashboard.ts`)
 * reuses this exact function to source the dashboard's headcount/joiners
 * tiles from the SAME real employee records this hook fetches, rather than
 * re-deriving its own employee-fetch logic.
 */
import * as React from 'react';
import { getTrpcClient, isForbiddenTrpcError } from '../lib/trpc.js';
import {
  useOAuthEntityContext,
  type OAuthEntityContext,
} from '../lib/auth/oauth_entity_context.js';
import {
  usePayCurrencyOptions,
  type PayCurrencyOption,
} from './use_pay_currencies.js';
import {
  deriveEmployeesFromRecords,
  type RealEmployeeRecord,
} from './wagepro_store_employees.js';
import type { Employee } from './types';

/** Input the real fetch needs to identify the entity — same shape every `payroll.*` query takes. */
export interface EmployeeRecordsFetchInput {
  firm_id: string;
  entity_id: string;
}

/**
 * The one `payroll.listEmployees` call {@link fetchEmployeeRecords} needs,
 * isolated behind an injectable seam — mirrors `use_pay_runs.ts`'s
 * `PayRunsFetchDeps` / `defaultPayRunsFetchDeps()` DI pattern.
 */
export interface EmployeeRecordsFetchDeps {
  listEmployees: (
    input: EmployeeRecordsFetchInput,
  ) => Promise<{ employees: RealEmployeeRecord[] }>;
}

function defaultEmployeeRecordsFetchDeps(): EmployeeRecordsFetchDeps {
  const client = getTrpcClient();
  return {
    listEmployees: (input) => client.payroll.listEmployees.query(input),
  };
}

export type EmployeeRecordsFetchResult =
  | { outcome: 'success'; records: RealEmployeeRecord[] }
  | { outcome: 'failure'; failure: EmployeesLoadFailure };

/** The two ways a real `payroll.listEmployees` fetch can fail, mirroring `MyPayLoadFailure` (`use_my_pay.ts`). */
export type EmployeesLoadFailure = 'forbidden' | 'error';

export type EmployeesState =
  | { status: 'loading' }
  | { status: 'no_entity' }
  | { status: 'entitlement_required' }
  | { status: 'error' }
  | { status: 'ready'; employees: Employee[] };

/**
 * Pure decision function, exported separately from the hook below so it is
 * unit-testable without rendering React (mirrors `resolveMyPayState`/
 * `resolveSessionFactsState`'s "pure decision function + thin hook" split).
 *
 * Precedence: `no_entity` always wins (a caller whose grant just lost its
 * entity binding must see `no_entity`, not a leftover `error`/
 * `entitlement_required` from before the context changed) — then resolved
 * `records` win over a `failure` flag (a successful retry's records must
 * display even if a stale `failure` from an earlier attempt was never
 * explicitly cleared) — then `entitlement_required` — then `error` — then
 * `loading`. In the real hook, `records`/`failure` are reset together at
 * the start of every fetch, so they are never actually both set at once;
 * this ordering only matters for a synthetic combination a test might
 * construct directly.
 */
export function resolveEmployeesState(input: {
  entityContext: OAuthEntityContext;
  records: RealEmployeeRecord[] | undefined;
  failure: EmployeesLoadFailure | undefined;
}): EmployeesState {
  if (input.entityContext.status === 'no_entity') {
    return { status: 'no_entity' };
  }
  if (input.records !== undefined) {
    return {
      status: 'ready',
      employees: deriveEmployeesFromRecords(input.records),
    };
  }
  if (input.failure === 'forbidden') {
    return { status: 'entitlement_required' };
  }
  if (input.failure === 'error') {
    return { status: 'error' };
  }
  return { status: 'loading' };
}

/** Classifies a caught `payroll.listEmployees` error into the two `EmployeesLoadFailure` buckets `resolveEmployeesState` understands. */
function classifyFailure(err: unknown): EmployeesLoadFailure {
  return isForbiddenTrpcError(err) ? 'forbidden' : 'error';
}

/**
 * Resolves the entity's real employee directory records, or classifies why
 * it couldn't. Exported (and DI'd via `deps`) so the FORBIDDEN
 * classification is unit-testable against a fake client that throws a
 * real-shaped tRPC error — mirrors `use_pay_runs.ts`'s `fetchPayRunRecords`.
 * `useEmployeesState()` below is a thin caller: it owns React state and
 * calls this function with the real client (`deps` defaults to
 * {@link defaultEmployeeRecordsFetchDeps}). `use_dashboard.ts` (C4, #22328)
 * is the other caller — it reuses this exact function rather than
 * re-deriving its own employee-fetch logic for the dashboard's
 * headcount/joiners tiles.
 *
 * `deps` defaults to `undefined` and is resolved INSIDE the `try` below
 * (never as the parameter's own default-value expression, which runs before
 * the function body) — a synchronous `getTrpcClient()` throw there used to
 * escape this function's own `try` entirely (adversarial review finding,
 * C4 #22328, surfaced via `use_dashboard.ts`'s `Promise.all`/`void load()`
 * reuse of this same function). `isCancelled` lets a caller suppress the
 * `console.error` below for a fetch whose own effect already
 * unmounted/re-ran by the time this rejects — mirrors
 * `use_session_facts.ts`'s/`use_pay_run_audit.ts`'s
 * `if (cancelled) return;`-before-`console.error` stance.
 */
export async function fetchEmployeeRecords(
  input: EmployeeRecordsFetchInput,
  deps?: EmployeeRecordsFetchDeps,
  isCancelled: () => boolean = () => false,
): Promise<EmployeeRecordsFetchResult> {
  try {
    const resolvedDeps = deps ?? defaultEmployeeRecordsFetchDeps();
    const result = await resolvedDeps.listEmployees(input);
    return { outcome: 'success', records: result.employees };
  } catch (err) {
    if (!isCancelled()) {
      console.error(
        '[wagepro-web] Failed to resolve the real employee directory:',
        err,
      );
    }
    return { outcome: 'failure', failure: classifyFailure(err) };
  }
}

interface UseEmployeesStateResult {
  state: EmployeesState;
  /**
   * The raw, unmapped records this render's fetch resolved to (or
   * `undefined` before the fetch resolves) -- kept alongside `state`
   * (rather than folded into `EmployeesState` itself) so `useEmployees()`/
   * `useEmployee()`'s public return shape stays byte-for-byte unchanged
   * (#22335 addition). {@link useEmployeeRecord} is the one consumer that
   * needs the raw shape (the edit form needs the unmasked bank fields the
   * derived `Employee` UI type discards; `pay_basis`/`hourly_rate_cents` are
   * carried through to the UI type as of #24396, so they are no longer a
   * reason to reach for the raw record).
   */
  records: RealEmployeeRecord[] | undefined;
  /** Re-runs the fetch from scratch. Only meaningful for the `error`/`entitlement_required` states. */
  retry: () => void;
}

/**
 * The shared underlying fetch both `useEmployees()` and `useEmployee()`
 * build on. Each call site gets its OWN independent `useEffect`/fetch
 * instance (no cross-component cache) — the same "self-fetching, no store
 * seam" shape `use_my_pay.ts` establishes.
 */
function useEmployeesState(): UseEmployeesStateResult {
  const entityContext = useOAuthEntityContext();
  const [records, setRecords] = React.useState<
    RealEmployeeRecord[] | undefined
  >(undefined);
  const [failure, setFailure] = React.useState<
    EmployeesLoadFailure | undefined
  >(undefined);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (entityContext.status !== 'ready') return;
    // Captured as its own consts so the nested async closure below
    // references definitely-string values rather than relying on narrowing
    // of `entityContext` (a hook return value) surviving across the closure
    // boundary -- same pattern `use_session_facts.ts`/`use_my_pay.ts` use.
    const firmId: string = entityContext.firm_id;
    const entityId: string = entityContext.entity_id;
    let cancelled = false;
    setRecords(undefined);
    setFailure(undefined);

    async function load(): Promise<void> {
      // All catch-block classification lives in `fetchEmployeeRecords` --
      // this hook only owns React state. See that function's docblock for
      // why the split exists. `() => cancelled` suppresses that function's
      // own `console.error` for a fetch that settles after this effect
      // already re-ran/unmounted.
      const result = await fetchEmployeeRecords(
        { firm_id: firmId, entity_id: entityId },
        undefined,
        () => cancelled,
      );
      if (cancelled) return;
      if (result.outcome === 'success') {
        setRecords(result.records);
      } else {
        setFailure(result.failure);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, reloadToken]);

  // Memoized on the actual inputs `resolveEmployeesState` reads, so a render
  // triggered by something else entirely (a parent re-render, an unrelated
  // state update elsewhere in the tree) reuses the SAME `state` object --
  // and, critically, the SAME `state.employees` array reference -- instead
  // of calling `deriveEmployeesFromRecords` again and handing back a
  // fresh array of fresh objects every render. Without this, `useEmployees()`
  // could never return a referentially-stable `employees` array, which
  // defeats the directory screen's own
  // `useMemo([employees, search_query, direction, locale])` -- it would see
  // a "changed" `employees` dependency on every render and recompute the
  // filter/sort from scratch regardless of whether anything actually
  // changed (PR #22369 adversarial review finding #6).
  const state = React.useMemo(
    () => resolveEmployeesState({ entityContext, records, failure }),
    [entityContext, records, failure],
  );

  return {
    state,
    records,
    retry: () => {
      setReloadToken((token) => token + 1);
    },
  };
}

export interface UseEmployeesResult {
  /**
   * The full `EmployeesState` discriminated union — the directory screen
   * renders each status distinctly (loading / no_entity /
   * entitlement_required / error / ready) rather than collapsing them all
   * down to an empty `employees` array (PR #22369 adversarial review
   * finding #1). Prefer this over `employees`/`is_loading` in new code.
   */
  state: EmployeesState;
  /** Convenience projection: `[]` for every non-`ready` state. Kept for callers that only care about the resolved list. */
  employees: Employee[];
  is_loading: boolean;
  /** Re-runs the fetch from scratch — wired to the `error` state's "try again" action. */
  retry: () => void;
  /**
   * Pay-currency picker options for the employee form (`payroll.listPayCurrencies`);
   * `undefined` while loading / after a failed fetch (the form then falls
   * back to base + the current code). Optional so test doubles may omit it.
   */
  pay_currencies?: readonly PayCurrencyOption[] | undefined;
}

/**
 * The full employee directory for the active company. `employees` is `[]`
 * for every non-`ready` state (loading, no entity, missing entitlement,
 * fetch error) — callers that need to distinguish those states (e.g. to
 * render a genuine loading skeleton or an entitlement/error message instead
 * of a blanket "no employees" empty state) should read `state` instead.
 */
/**
 * The raw `RealEmployeeRecord[]` for the current entity — the shape callers
 * need when the derived `Employee` UI type has discarded the field they want.
 *
 * Exists for `usePayRuns`, which needs `full_name` to label pay-run lines:
 * `derivePayRunsFromRecords` was being called with a hardcoded `undefined`
 * for its `employeeRecords` argument, so EVERY line's `employee_name`
 * degraded to the `'—'` fallback and the post-run "Generate payslips"
 * dropdown listed five identical em dashes with no way to tell the employees
 * apart.
 *
 * Same self-fetching, no-cache shape as `useEmployees()` — each call site
 * gets its own fetch (see `useEmployeesState`'s doc comment).
 */
export function useEmployeeRecords(): {
  records: RealEmployeeRecord[] | undefined;
  retry: () => void;
} {
  const { records, retry } = useEmployeesState();
  return { records, retry };
}

export function useEmployees(): UseEmployeesResult {
  const { state, retry } = useEmployeesState();
  const pay_currencies = usePayCurrencyOptions();
  return {
    pay_currencies,
    state,
    employees: state.status === 'ready' ? state.employees : [],
    is_loading: state.status === 'loading',
    retry,
  };
}

export interface UseEmployeeResult {
  employee: Employee | null;
  is_loading: boolean;
  /** The full `EmployeesState` the lookup was resolved against — lets the profile screen distinguish `entitlement_required`/`error`/`no_entity` from a genuine not-found id (PR #22369 adversarial review finding #4). */
  state: EmployeesState;
  /** Re-runs the underlying fetch from scratch. */
  retry: () => void;
}

/**
 * Pure lookup — the actual bug fix this hook exists to ship, extracted out
 * of `useEmployee()` so it's unit-testable without rendering React. Returns
 * the matching employee, or `null` for ANY unmatched id — NEVER
 * `employees.at(0)`.
 *
 * The old implementation here did `match ?? employees.at(0) ?? null`,
 * claiming the fallback existed so "a bookmarked profile URL with a stale
 * id still renders a profile rather than a dead end." Against the real
 * directory this hook is now wired to, that justification is actively
 * dangerous: a stale, deleted, or simply hostile/guessed employee id would
 * silently render a DIFFERENT employee's real payroll record — salary,
 * bank account number, tax identifiers — to whoever is looking at the
 * profile screen. That is a live IDOR-shaped privacy leak, not a cosmetic
 * UX nicety, and no fallback justifies it. `employee_profile_screen.tsx`
 * already renders an explicit "not found" `EmptyState` whenever `employee`
 * is `null`; that state is now the ONLY thing an unmatched id ever
 * produces. See `use_employees.test.ts`'s regression test.
 */
export function findEmployeeById(
  employees: readonly Employee[],
  employee_id: string | null,
): Employee | null {
  return employees.find((employee) => employee.id === employee_id) ?? null;
}

/**
 * A single employee by id, resolved from the real directory (see module
 * docblock). Returns an EXPLICIT not-found state (`employee: null`) for an
 * id that does not match any record in a resolved, non-empty directory —
 * see {@link findEmployeeById}.
 */
export function useEmployee(employee_id: string | null): UseEmployeeResult {
  const { state, retry } = useEmployeesState();
  if (state.status !== 'ready') {
    return {
      employee: null,
      is_loading: state.status === 'loading',
      state,
      retry,
    };
  }
  return {
    employee: findEmployeeById(state.employees, employee_id),
    is_loading: false,
    state,
    retry,
  };
}

/**
 * Pure lookup over the RAW `payroll.listEmployees` records -- the edit form's
 * counterpart to {@link findEmployeeById}. Returns `null` for any unmatched
 * id, same "never fall back to a different employee's record" stance as
 * {@link findEmployeeById} (#22335 reuses that IDOR-hardening precedent, not
 * just its shape).
 */
export function findEmployeeRecordById(
  records: readonly RealEmployeeRecord[],
  employee_id: string | null,
): RealEmployeeRecord | null {
  return records.find((record) => record.id === employee_id) ?? null;
}

export interface UseEmployeeRecordResult {
  /** The raw record backing this employee -- carries the unmasked bank fields the derived `Employee` UI type discards (`pay_basis`/`hourly_rate_cents` are projected onto the UI type as of #24396). `null` while loading, on any non-`ready` state, or for an unmatched id. */
  record: RealEmployeeRecord | null;
  is_loading: boolean;
  /** The full `EmployeesState` the lookup was resolved against -- lets the edit form distinguish `entitlement_required`/`error` from a genuine not-found id, mirroring `useEmployee`'s own `state` field. */
  state: EmployeesState;
  /** Re-runs the underlying fetch from scratch. */
  retry: () => void;
  /**
   * Pay-currency picker options for the employee form (`payroll.listPayCurrencies`);
   * `undefined` while loading / after a failed fetch (the form then falls
   * back to base + the current code). Optional so test doubles may omit it.
   */
  pay_currencies?: readonly PayCurrencyOption[] | undefined;
}

/**
 * A single employee's RAW record by id (#22335) -- the edit form's
 * data source. Shares the same underlying fetch as {@link useEmployees}/
 * {@link useEmployee} (each call site gets its own independent fetch
 * instance, per this module's established "no cross-component cache" shape)
 * but surfaces the pre-`deriveEmployeesFromRecords` wire shape instead of the
 * UI-projected `Employee` type.
 */
export function useEmployeeRecord(
  employee_id: string | null,
): UseEmployeeRecordResult {
  const { state, records, retry } = useEmployeesState();
  const pay_currencies = usePayCurrencyOptions();
  if (state.status !== 'ready' || records === undefined) {
    return {
      pay_currencies,
      record: null,
      is_loading: state.status === 'loading',
      state,
      retry,
    };
  }
  return {
    pay_currencies,
    record: findEmployeeRecordById(records, employee_id),
    is_loading: false,
    state,
    retry,
  };
}
